import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import {
  Flex,
  Heading,
  Select,
  Switch,
  Text,
  TextField,
} from '@radix-ui/themes';
import {
  SessionRecapHelper,
  ZONE_METRICS,
} from 'classes/helpers/session-recap.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { ChartTooltip } from 'components/sections/analytics/session-hits/recap/visualizations/chart-tooltip';
import { ChartDots } from 'components/sections/analytics/session-hits/recap/visualizations/dots';
import { useRecapHover } from 'components/sections/analytics/session-hits/recap/visualizations/use-recap-hover';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import {
  DIVERGING_GRADIENT,
  HEATMAP_GRADIENT,
  NEUTRAL_RGB,
} from 'enums/session-recap/colors';
import {
  RecapZoneFilterKind,
  RecapZoneOverlay,
  ZoneMetric,
} from 'enums/session-recap/enums';
import { MAP_ZONE_METRIC_FAMILY_TO_LABEL } from 'enums/session-recap/labels';
import { t } from 'i18next';
import { IChartDot } from 'interfaces/session-recap/i-chart-dot';
import { IHeatmapBounds } from 'interfaces/session-recap/i-heatmap-bounds';
import { IHeatmapGrid } from 'interfaces/session-recap/i-heatmap-grid';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { IZoneCell } from 'interfaces/session-recap/i-zone-cell';
import { IZoneMetricDef } from 'interfaces/session-recap/i-zone-metric-def';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { useMemo, useRef, useState } from 'react';
import useMiscStore from 'stores/use-misc-store';

/**
 * svg user units per foot, the same in both axes so the box matches the
 * replay overlay (ReplayCanvas.drawStrikeZone)
 */
const SCALE = 100;

const DOT_RADIUS = 6.5;

/** space beside the 5x5 grid, in feet */
const GRID_PAD_FT = 0.3;
/** extra room so a heatmap blob can fade past the outer box */
const HEAT_MARGIN_FT = 0.45;
/** less room above the outer box than at the sides, so the card stays shorter */
const TOP_PAD_FT = 0.48;
/** minimum clearance if a zone bottom sits on the ground */
const PLATE_GAP_FT = 0.22;
/**
 * share of the real gap from the outer box down to the ground. Shorter than
 * knee height so the zone and field can draw larger.
 */
const PLATE_GAP_KEEP = 0.58;
const PLATE_BOTTOM_PAD_FT = 0.06;
/** svg units below the plate so a very low pitch is not clipped by the frame */
const LOW_PITCH_PAD = 16;

/** home plate as seen from the catcher, from the design */
const PLATE_PATH = 'M0 11L5.44762 0H163.429L168.876 11L84.4381 24L0 11Z';
const PLATE_PATH_WIDTH = 168.876;
const PLATE_PATH_HEIGHT = 24;

interface IFrame {
  width: number;
  height: number;
  toX: (ft: number) => number;
  toY: (ft: number) => number;
  /** 5x5 grid, svg units */
  grid: { x: number; y: number; width: number; height: number };
  cellW: number;
  cellH: number;
  /** bottom of the 5x5 box, feet; y is compressed below this */
  gridBottomFt: number;
  plate: { x: number; y: number; scale: number };
  heatmap: IHeatmapBounds;
}

const makeFrame = (szDef: IStrikeZone): IFrame => {
  const cellWFt = (szDef.right_ft - szDef.left_ft) / 3;
  const cellHFt = (szDef.top_ft - szDef.bottom_ft) / 3;

  const gridLeftFt = szDef.left_ft - cellWFt;
  const gridRightFt = szDef.right_ft + cellWFt;
  const gridTopFt = szDef.top_ft + cellHFt;
  const gridBottomFt = szDef.bottom_ft - cellHFt;

  const gridW = (gridRightFt - gridLeftFt) * SCALE;
  const cellW = cellWFt * SCALE;

  // the plate's widest corners line up with the zone edges
  const plateScale = (cellW * 3) / PLATE_PATH_WIDTH;
  const plateH = PLATE_PATH_HEIGHT * plateScale;

  const xMinFt = gridLeftFt - GRID_PAD_FT - HEAT_MARGIN_FT;
  const xMaxFt = gridRightFt + GRID_PAD_FT + HEAT_MARGIN_FT;
  const zMaxFt = gridTopFt + TOP_PAD_FT;

  const toX = (ft: number) => (ft - xMinFt) * SCALE;
  const linearY = (ft: number) => (zMaxFt - ft) * SCALE;
  const gridBottomY = linearY(gridBottomFt);
  const groundGap = (linearY(0) - gridBottomY) * PLATE_GAP_KEEP;
  const toY = (ft: number) => {
    if (ft >= gridBottomFt) {
      return linearY(ft);
    }

    const t = (gridBottomFt - ft) / Math.max(gridBottomFt, 0.01);
    return gridBottomY + t * groundGap;
  };

  const grid = {
    x: toX(gridLeftFt),
    y: toY(gridTopFt),
    width: gridW,
    height: (gridTopFt - gridBottomFt) * SCALE,
  };

  // the plate is on the ground (z = 0), drawn closer than knee height
  const plateY = Math.max(toY(0), grid.y + grid.height + PLATE_GAP_FT * SCALE);

  return {
    width: (xMaxFt - xMinFt) * SCALE,
    height: plateY + plateH + PLATE_BOTTOM_PAD_FT * SCALE + LOW_PITCH_PAD,
    toX,
    toY,
    grid,
    cellW,
    cellH: cellHFt * SCALE,
    gridBottomFt,
    plate: {
      x: grid.x + cellW,
      y: plateY,
      scale: plateScale,
    },
    heatmap: {
      xMinFt: xMinFt + 0.05,
      xMaxFt: xMaxFt - 0.05,
      // ground is z = 0; stop just inside the frame under the plate
      zMinFt:
        -(plateH / SCALE + PLATE_BOTTOM_PAD_FT + LOW_PITCH_PAD / SCALE) + 0.04,
      zMaxFt: zMaxFt - 0.05,
    },
  };
};

/** the inner 3x3 is the strike zone; the outer ring has fills but no dividers */
const isInnerZone = (c: IZoneCell) =>
  c.row >= 1 && c.row <= 3 && c.col >= 1 && c.col <= 3;

const cellRect = (frame: IFrame, c: IZoneCell) => ({
  x: frame.grid.x + c.col * frame.cellW,
  y: frame.grid.y + c.row * frame.cellH,
  width: frame.cellW,
  height: frame.cellH,
});

const ZoneHits = (props: {
  cells: IZoneCell[];
  frame: IFrame;
  selected: { row: number; col: number } | null;
  onSelect: (row: number, col: number) => void;
}) => (
  <g>
    {props.cells.map((c) => {
      const selected =
        props.selected?.row === c.row && props.selected.col === c.col;

      return (
        <rect
          key={`${c.row}-${c.col}`}
          {...cellRect(props.frame, c)}
          className="recap-zone-hit"
          aria-label={t('hitters.recap-filter-zone').toString()}
          aria-pressed={selected}
          role="button"
          onClick={() => props.onSelect(c.row, c.col)}
        />
      );
    })}
  </g>
);

const ZoneCells = (props: {
  cells: IZoneCell[];
  metric: IZoneMetricDef;
  frame: IFrame;
  selected: { row: number; col: number } | null;
}) => {
  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  return (
    <g
      className="recap-zone-cells"
      pointerEvents="none"
      data-active={props.selected ? true : undefined}
    >
      {props.cells.map((c) => {
        if (c.value === null) {
          return null;
        }

        const selected =
          props.selected?.row === c.row && props.selected.col === c.col;

        return (
          <rect
            key={`${c.row}-${c.col}`}
            {...cellRect(props.frame, c)}
            fill={SessionRecapHelper.getDivergingColor(
              c.value,
              props.metric,
              props.metric.higherIsBetter
            )}
            fillOpacity={SessionRecapHelper.getDivergingOpacity(
              c.value,
              props.metric,
              props.metric.higherIsBetter,
              !isDarkMode
            )}
            className={
              isInnerZone(c) ? 'recap-zone-cell' : 'recap-zone-cell-outer'
            }
            data-selected={selected || undefined}
          />
        );
      })}
    </g>
  );
};

/** drawn above the dots so values stay readable */
const ZoneLabels = (props: {
  cells: IZoneCell[];
  metric: IZoneMetricDef;
  frame: IFrame;
  selected: { row: number; col: number } | null;
}) => {
  const { frame } = props;
  const fontSize = Math.min(frame.cellW, frame.cellH) * 0.3;

  return (
    <g
      className="recap-zone-labels"
      pointerEvents="none"
      data-active={props.selected ? true : undefined}
    >
      {props.cells.map((c) => {
        if (c.value === null) {
          return null;
        }

        const r = cellRect(frame, c);
        const selected =
          props.selected?.row === c.row && props.selected.col === c.col;

        return (
          <text
            key={`${c.row}-${c.col}`}
            x={r.x + r.width / 2}
            y={r.y + r.height / 2}
            className="recap-zone-label"
            data-selected={selected || undefined}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={fontSize}
          >
            {props.metric.format(c.value)}
          </text>
        );
      })}
    </g>
  );
};

/** z at an svg y, matching the compressed scale below the outer box */
const zAtY = (frame: IFrame, y: number) => {
  const bounds = frame.heatmap;
  const yTop = frame.toY(bounds.zMaxFt);
  const ySeam = frame.toY(frame.gridBottomFt);
  const yGround = frame.toY(0);

  if (y <= ySeam) {
    const span = Math.max(ySeam - yTop, 0.01);
    const drop = bounds.zMaxFt - frame.gridBottomFt;
    return bounds.zMaxFt - ((y - yTop) / span) * drop;
  }

  const t = (y - ySeam) / Math.max(yGround - ySeam, 0.01);
  return frame.gridBottomFt * (1 - t);
};

const heatmapHref = (
  grid: IHeatmapGrid,
  metric: IZoneMetricDef,
  frame: IFrame,
  colorScale: { min: number; max: number } | null
) => {
  const bounds = frame.heatmap;
  const canvas = document.createElement('canvas');
  canvas.width = grid.cols;
  canvas.height = grid.rows;

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return undefined;
  }

  const image = ctx.createImageData(grid.cols, grid.rows);
  const yTop = frame.toY(bounds.zMaxFt);
  const yBot = frame.toY(bounds.zMinFt);
  const zSpan = bounds.zMaxFt - bounds.zMinFt;

  for (let row = 0; row < grid.rows; row++) {
    const y = yTop + ((row + 0.5) / grid.rows) * (yBot - yTop);
    const src = ((bounds.zMaxFt - zAtY(frame, y)) / zSpan) * grid.rows - 0.5;
    const row0 = Math.floor(src);
    const row1 = Math.min(grid.rows - 1, row0 + 1);
    const blend = src - row0;

    for (let col = 0; col < grid.cols; col++) {
      const sample = (r: number) => {
        if (r < 0 || r >= grid.rows) {
          return null;
        }

        const i = r * grid.cols + col;
        return Number.isNaN(grid.values[i]) ? null : i;
      };

      const a = sample(row0);
      const b = sample(row1);
      const pick = a ?? b;

      if (pick == null) {
        continue;
      }

      const i = a != null && b != null ? (blend < 0.5 ? a : b) : pick;
      const [red, green, blue] = colorScale
        ? SessionRecapHelper.getHeatmapRGB(
            grid.values[i],
            colorScale,
            metric.higherIsBetter
          )
        : NEUTRAL_RGB;
      const pixel = (row * grid.cols + col) * 4;

      image.data[pixel] = red;
      image.data[pixel + 1] = green;
      image.data[pixel + 2] = blue;
      image.data[pixel + 3] = Math.round(grid.alphas[i] * 255);
    }
  }

  ctx.putImageData(image, 0, 0);

  // upscale and blur so the inch grid reads as a continuous blob
  const scale = 4;
  const smooth = document.createElement('canvas');
  smooth.width = grid.cols * scale;
  smooth.height = grid.rows * scale;
  const smoothCtx = smooth.getContext('2d');

  if (!smoothCtx) {
    return canvas.toDataURL();
  }

  smoothCtx.imageSmoothingEnabled = true;
  smoothCtx.imageSmoothingQuality = 'high';
  smoothCtx.filter = 'blur(6px)';
  smoothCtx.drawImage(canvas, 0, 0, smooth.width, smooth.height);
  return smooth.toDataURL();
};

const HeatmapOverlay = (props: {
  grid: IHeatmapGrid;
  metric: IZoneMetricDef;
  frame: IFrame;
  scale: { min: number; max: number } | null;
}) => {
  const { frame, metric } = props;
  const bounds = frame.heatmap;

  const href = useMemo(
    () => heatmapHref(props.grid, metric, frame, props.scale),
    [props.grid, metric, frame, props.scale]
  );

  if (!href) {
    return null;
  }

  return (
    <image
      href={href}
      x={frame.toX(bounds.xMinFt)}
      y={frame.toY(bounds.zMaxFt)}
      width={(bounds.xMaxFt - bounds.xMinFt) * SCALE}
      height={frame.toY(bounds.zMinFt) - frame.toY(bounds.zMaxFt)}
      preserveAspectRatio="none"
      className="recap-heatmap"
    />
  );
};

export const ZoneMetricLegend = (props: {
  metric: IZoneMetricDef;
  heatmap?: boolean;
  scale: { min: number; max: number } | null;
}) => {
  const { metric } = props;
  const stops = props.heatmap ? HEATMAP_GRADIENT : DIVERGING_GRADIENT;

  return (
    <Flex direction="column" gap="1" className="recap-gradient-legend">
      <div
        className="recap-gradient-bar"
        style={{
          background: `linear-gradient(to ${
            metric.higherIsBetter ? 'right' : 'left'
          }, ${stops.join(', ')})`,
        }}
      />
      <Flex justify="between">
        <Text size="1" color={RADIX.COLOR.SECONDARY}>
          {metric.format(
            props.heatmap && props.scale ? props.scale.min : metric.min
          )}
        </Text>
        {!props.heatmap && metric.showBaseline !== false && (
          <Text size="1" color={RADIX.COLOR.SECONDARY}>
            {metric.format(metric.baseline)}
          </Text>
        )}
        <Text size="1" color={RADIX.COLOR.SECONDARY}>
          {props.heatmap && props.scale
            ? metric.format(props.scale.max)
            : `${metric.format(metric.max)}+`}
        </Text>
      </Flex>
    </Flex>
  );
};

interface IProps {
  /** Every pitch in the current recap filters. Zone and heatmap colours use this. */
  records: ISessionRecapHit[];
  /** Legend- and zone-filtered pitches drawn as dots. */
  shots: ISessionRecapHit[];
  szDef: IStrikeZone;
  highlightKey?: string | null;
  /** locks the overlay and hides the shots toggle and overlay menu */
  overlayID?: RecapZoneOverlay;
  /**
   * used with overlay. Shots stay on for a shots-only chart and off for an
   * overlay, except the hovered or selected pitch.
   */
  showShots?: boolean;
}

const SORTED_METRICS = SessionRecapHelper.getSortedZoneMetrics();

export const RecapStrikeZoneView = (props: IProps) => {
  const { szDef } = props;

  const colorByID = useAnalyticsStore((s) => s.colorByID);
  const zoneMetricID = useAnalyticsStore((s) => s.zoneMetricID);
  const storedOverlayID = useAnalyticsStore((s) => s.zoneOverlayID);
  const storedShowShots = useAnalyticsStore((s) => s.showShots);
  const zoneFilter = useAnalyticsStore((s) => s.zoneFilter);
  const selectedPlate =
    zoneFilter?.kind === RecapZoneFilterKind.Plate ? zoneFilter : null;

  const locked = props.overlayID !== undefined;
  const zoneOverlayID = props.overlayID ?? storedOverlayID;
  const showShots = locked
    ? (props.showShots ?? props.overlayID === RecapZoneOverlay.None)
    : storedShowShots;

  const hover = useRecapHover();
  const metricSearchRef = useRef<HTMLInputElement>(null);
  const [metricQuery, setMetricQuery] = useState('');

  const metricSections = useMemo(() => {
    const query = metricQuery.trim().toLowerCase();

    if (!query) {
      // not filtering so return all sections
      return SessionRecapHelper.getZoneMetricSections(SORTED_METRICS);
    }

    // match user's input against translated labels
    const filtered = SORTED_METRICS.filter((m) =>
      t(m.label).toString().toLowerCase().includes(query)
    );

    return SessionRecapHelper.getZoneMetricSections(filtered);
  }, [metricQuery]);

  const frame = useMemo(() => makeFrame(szDef), [szDef]);

  const metric = useMemo(
    () => ZONE_METRICS.find((m) => m.id === zoneMetricID) ?? ZONE_METRICS[0],
    [zoneMetricID]
  );

  const isZones = zoneOverlayID === RecapZoneOverlay.Zones;
  const isHeatmap = zoneOverlayID === RecapZoneOverlay.Heatmap;
  const hasOverlay = zoneOverlayID !== RecapZoneOverlay.None;
  const title = locked
    ? `${t('tr.strike-zone')} · ${t(
        isZones
          ? 'hitters.recap-overlay-zones'
          : isHeatmap
            ? 'hitters.recap-overlay-heatmap'
            : 'common.shots'
      )}`
    : t('tr.strike-zone');

  const cells = useMemo(
    () =>
      isZones
        ? SessionRecapHelper.getZoneCells(props.records, metric, szDef)
        : [],
    [isZones, props.records, metric, szDef]
  );

  const heatmapGrid = useMemo(
    () =>
      isHeatmap
        ? SessionRecapHelper.getHeatmap(props.records, metric, frame.heatmap)
        : null,
    [isHeatmap, props.records, metric, frame]
  );

  const colorScale = useMemo(() => {
    if (!isHeatmap || !heatmapGrid) {
      return null;
    }

    return SessionRecapHelper.getSessionColorScale(
      heatmapGrid.values.filter((v) => !Number.isNaN(v))
    );
  }, [isHeatmap, heatmapGrid]);

  const dots = useMemo(
    () =>
      props.shots.reduce((acc, r) => {
        const plate = r.hit.pitch?.plate;

        if (!plate) {
          return acc;
        }

        const dotColor = SessionRecapHelper.getColorForDot(r, colorByID);

        acc.push({
          id: r.hit._id,
          x: Math.min(
            frame.width - DOT_RADIUS,
            Math.max(DOT_RADIUS, frame.toX(plate.plate_x))
          ),
          y: Math.min(
            frame.height - DOT_RADIUS - LOW_PITCH_PAD,
            Math.max(DOT_RADIUS, frame.toY(plate.plate_z))
          ),
          color: dotColor.color,
          muted: dotColor.muted,
          legendKey: SessionRecapHelper.getLegendKey(r, colorByID),
        });
        return acc;
      }, [] as IChartDot[]),
    [props.shots, colorByID, frame]
  );

  const visibleDots = useMemo(() => {
    if (showShots) {
      return dots;
    }

    return dots.filter(
      (d) => d.id === hover.hoveredHitID || d.id === hover.selectedHitID
    );
  }, [showShots, dots, hover.hoveredHitID, hover.selectedHitID]);

  const hoveredRecord =
    hover.pointer && hover.hoveredHitID
      ? props.records.find((r) => r.hit._id === hover.hoveredHitID)
      : undefined;

  const { grid, cellW, cellH } = frame;
  const zone = {
    x: grid.x + cellW,
    y: grid.y + cellH,
    width: cellW * 3,
    height: cellH * 3,
  };

  return (
    <ErrorBoundary identifier="RecapStrikeZoneView">
      <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
        <Flex
          justify="between"
          align="center"
          gap={RADIX.FLEX.GAP.SM}
          className="recap-chart-header"
        >
          <Heading size={RADIX.HEADING.SIZE.SM}>{title}</Heading>

          {(!locked || hasOverlay) && (
            <Flex gap="2" align="center" wrap="wrap" justify="end">
              {!locked && (
                // todo: this should be a CommonSwitchButton instead
                <Text as="label" size="1" className="recap-shots-toggle">
                  <Flex gap="2" align="center">
                    {t('common.shots')}
                    <Switch
                      size="1"
                      checked={showShots}
                      onCheckedChange={(v) =>
                        useAnalyticsStore.setState({
                          showShots: v,
                        })
                      }
                    />
                  </Flex>
                </Text>
              )}

              {!locked && (
                // todo: this should be a CommonSelectInput instead
                <Select.Root
                  size="1"
                  value={zoneOverlayID}
                  onValueChange={(v) => {
                    const next = v as RecapZoneOverlay;
                    const turningOn =
                      storedOverlayID === RecapZoneOverlay.None &&
                      next !== RecapZoneOverlay.None;

                    useAnalyticsStore.setState({
                      zoneOverlayID: next,
                      zoneMetricID: turningOn
                        ? SORTED_METRICS[0].id
                        : zoneMetricID,
                    });
                  }}
                >
                  <Select.Trigger variant="soft" color={RADIX.COLOR.NEUTRAL} />
                  <Select.Content position="popper" side="bottom" align="end">
                    <Select.Item value={RecapZoneOverlay.None}>
                      {t('hitters.recap-overlay-none')}
                    </Select.Item>
                    <Select.Item value={RecapZoneOverlay.Zones}>
                      {t('hitters.recap-overlay-zones')}
                    </Select.Item>
                    <Select.Item value={RecapZoneOverlay.Heatmap}>
                      {t('hitters.recap-overlay-heatmap')}
                    </Select.Item>
                  </Select.Content>
                </Select.Root>
              )}

              {(!locked || hasOverlay) && (
                // todo: this should be a CommonSearchInput instead
                <Select.Root
                  size="1"
                  value={hasOverlay ? zoneMetricID : ''}
                  disabled={!hasOverlay}
                  onValueChange={(v) =>
                    useAnalyticsStore.setState({
                      zoneMetricID: v as ZoneMetric,
                    })
                  }
                  onOpenChange={(open) => {
                    if (open) {
                      window.setTimeout(
                        () => metricSearchRef.current?.focus(),
                        50
                      );
                    } else {
                      setMetricQuery('');
                    }
                  }}
                >
                  <Select.Trigger
                    variant="soft"
                    color={RADIX.COLOR.NEUTRAL}
                    placeholder={t('common.na').toString()}
                  />
                  <Select.Content position="popper" side="bottom" align="end">
                    <div
                      className="recap-metric-search"
                      onKeyDown={(event) => {
                        if (
                          event.key.length === 1 ||
                          event.key === 'Backspace' ||
                          event.key === 'Delete'
                        ) {
                          event.stopPropagation();
                        }
                      }}
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <TextField.Root
                        ref={metricSearchRef}
                        size="1"
                        value={metricQuery}
                        placeholder={t('common.search-placeholder').toString()}
                        onChange={(event) => setMetricQuery(event.target.value)}
                        autoComplete="off"
                      >
                        <TextField.Slot>
                          <MagnifyingGlassIcon />
                        </TextField.Slot>
                      </TextField.Root>
                    </div>
                    {metricSections.length === 0 && (
                      <Text
                        size="1"
                        color={RADIX.COLOR.SECONDARY}
                        className="recap-metric-empty"
                      >
                        {t('common.no-results-found')}
                      </Text>
                    )}
                    {metricSections.map((section) => (
                      <Select.Group key={section.family}>
                        <Select.Label className="recap-metric-family">
                          {t(MAP_ZONE_METRIC_FAMILY_TO_LABEL[section.family])}
                        </Select.Label>
                        {section.metrics.map((m) => (
                          <Select.Item key={m.id} value={m.id}>
                            {t(m.label)}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    ))}
                    {hasOverlay &&
                      !metricSections.some((section) =>
                        section.metrics.some((m) => m.id === zoneMetricID)
                      ) && (
                        <Select.Item
                          value={zoneMetricID}
                          disabled
                          className="recap-metric-hidden"
                          style={{ display: 'none' }}
                        >
                          {t(metric.label)}
                        </Select.Item>
                      )}
                  </Select.Content>
                </Select.Root>
              )}
            </Flex>
          )}
        </Flex>

        <div
          className="recap-chart"
          data-overlay={hasOverlay && !zoneFilter}
          onPointerLeave={hover.clear}
        >
          <svg
            viewBox={`0 0 ${frame.width} ${frame.height}`}
            role="img"
            aria-label={title.toString()}
          >
            {isHeatmap && heatmapGrid && (
              <HeatmapOverlay
                grid={heatmapGrid}
                metric={metric}
                frame={frame}
                scale={colorScale}
              />
            )}

            {isZones && (
              <ZoneCells
                cells={cells}
                metric={metric}
                frame={frame}
                selected={selectedPlate}
              />
            )}

            <rect {...grid} className="recap-zone-outer" />

            {!isHeatmap &&
              [1, 2].map((i) => (
                <g key={i} pointerEvents="none">
                  <line
                    x1={zone.x + cellW * i}
                    x2={zone.x + cellW * i}
                    y1={zone.y}
                    y2={zone.y + zone.height}
                    className="recap-zone-grid"
                  />
                  <line
                    x1={zone.x}
                    x2={zone.x + zone.width}
                    y1={zone.y + cellH * i}
                    y2={zone.y + cellH * i}
                    className="recap-zone-grid"
                  />
                </g>
              ))}

            <rect {...zone} className="recap-zone-inner" />

            <path
              d={PLATE_PATH}
              transform={`translate(${frame.plate.x} ${frame.plate.y}) scale(${frame.plate.scale})`}
              className="recap-plate"
            />

            {isZones && (
              <ZoneHits
                cells={cells}
                frame={frame}
                selected={selectedPlate}
                onSelect={(row, col) => {
                  const current = useAnalyticsStore.getState().zoneFilter;
                  const same =
                    current?.kind === RecapZoneFilterKind.Plate &&
                    current.row === row &&
                    current.col === col;

                  useAnalyticsStore.setState({
                    zoneFilter: same
                      ? null
                      : { kind: RecapZoneFilterKind.Plate, row, col },
                  });
                }}
              />
            )}

            <ChartDots
              dots={visibleDots}
              hoveredID={hover.hoveredHitID}
              selectedID={hover.selectedHitID}
              onEnter={hover.onEnter}
              onLeave={hover.onLeave}
              onSelect={hover.onSelect}
              highlightKey={props.highlightKey}
            />

            {isZones && (
              <ZoneLabels
                cells={cells}
                metric={metric}
                frame={frame}
                selected={selectedPlate}
              />
            )}
          </svg>
        </div>

        {hasOverlay && (!isHeatmap || colorScale) && (
          <ZoneMetricLegend
            metric={metric}
            heatmap={isHeatmap}
            scale={colorScale}
          />
        )}
      </Flex>

      {hover.pointer && hoveredRecord && (
        <ChartTooltip record={hoveredRecord} pointer={hover.pointer} />
      )}
    </ErrorBoundary>
  );
};
